// F-F media panel for the exhibit/zone edit form: a tabbed surface for
// narration + supporting media that sits alongside the existing cover-image
// slot. F-F.2 shipped Narration + Audio; F-F.3 added Video; F-F.4 adds Docs
// (pdf). The Audio + Video + Docs tabs are one generic <MediaKindTab>
// parameterised by kind.
//
// Presentational + controlled — the owner-specific container (ExhibitMediaPanel
// / ZoneMediaPanel) wires the React Query mutations and passes callbacks down,
// so this component is owner-agnostic and shared by both forms.

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shared/components/ui/tabs";
import type { AssetKind, MediaItem } from "@shared/api/exhibits";
import type { AudioLanguage } from "@shared/hooks/useTextToSpeech";
import { CONTENT_LANGUAGES } from "@shared/lib/language";

import MediaKindTab from "./MediaKindTab";
import NarrationTab, { type NarrationRow } from "./NarrationTab";

// #3: small count pill shown on a Media tab so staff see what's attached
// without opening each tab.
const CountPill = ({ count, testId }: { count: string; testId: string }) => (
  <span
    className="ml-1.5 inline-flex items-center justify-center rounded-full bg-muted px-1.5 text-xs font-medium text-muted-foreground"
    data-testid={testId}
  >
    {count}
  </span>
);

export interface MediaPanelProps {
  ownerKey: string;
  assetUrl: (assetId: string) => string;
  disabled?: boolean;
  // narration
  narration: Record<AudioLanguage, NarrationRow>;
  generatingLang: AudioLanguage | null;
  onGenerate: (lang: AudioLanguage) => void;
  onUploadNarration: (lang: AudioLanguage, file: File) => void;
  onDownloadNarration: (lang: AudioLanguage) => void;
  onRemoveNarration: (lang: AudioLanguage) => void;
  onSaveVoiceUrl: (lang: AudioLanguage, url: string) => void;
  // per-kind media (audio + video + pdf + image)
  media: MediaItem[];
  uploadingMedia?: boolean;
  onUploadMedia: (params: {
    file: File;
    kind: AssetKind;
    language: string | null;
    caption: string | null;
  }) => void;
  onUpdateMedia: (
    assetId: string,
    patch: { caption: string | null; language: string | null },
  ) => void;
  onRemoveMedia: (assetId: string) => void;
}

const MediaPanel = ({
  ownerKey,
  assetUrl,
  disabled,
  narration,
  generatingLang,
  onGenerate,
  onUploadNarration,
  onDownloadNarration,
  onRemoveNarration,
  onSaveVoiceUrl,
  media,
  uploadingMedia,
  onUploadMedia,
  onUpdateMedia,
  onRemoveMedia,
}: MediaPanelProps) => {
  const kindCount = (kind: string) =>
    media.filter((m) => m.kind === kind).length;
  const narrationCount = CONTENT_LANGUAGES.filter(
    (l) => Boolean(narration[l]?.assetId) || Boolean(narration[l]?.voiceUrl),
  ).length;

  return (
    <Card data-testid={`media-panel-${ownerKey}`}>
      <CardHeader>
        <CardTitle className="font-heading">Media</CardTitle>
        <CardDescription>
          AI / custom narration and supporting images, audio, video &amp;
          documents for this {ownerKey}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="narration">
          <TabsList data-testid="media-tabs">
            <TabsTrigger value="narration" data-testid="media-tab-narration">
              Narration
              <CountPill
                count={`${narrationCount}/3`}
                testId="media-tab-narration-count"
              />
            </TabsTrigger>
            <TabsTrigger value="audio" data-testid="media-tab-audio">
              Audio
              {kindCount("audio") > 0 && (
                <CountPill
                  count={String(kindCount("audio"))}
                  testId="media-tab-audio-count"
                />
              )}
            </TabsTrigger>
            <TabsTrigger value="video" data-testid="media-tab-video">
              Video
              {kindCount("video") > 0 && (
                <CountPill
                  count={String(kindCount("video"))}
                  testId="media-tab-video-count"
                />
              )}
            </TabsTrigger>
            <TabsTrigger value="pdf" data-testid="media-tab-pdf">
              Docs
              {kindCount("pdf") > 0 && (
                <CountPill
                  count={String(kindCount("pdf"))}
                  testId="media-tab-pdf-count"
                />
              )}
            </TabsTrigger>
            <TabsTrigger value="image" data-testid="media-tab-image">
              Photos
              {kindCount("image") > 0 && (
                <CountPill
                  count={String(kindCount("image"))}
                  testId="media-tab-image-count"
                />
              )}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="narration" className="pt-4">
            <NarrationTab
              ownerKey={ownerKey}
              assetUrl={assetUrl}
              narration={narration}
              generatingLang={generatingLang}
              disabled={disabled}
              onGenerate={onGenerate}
              onUploadCustom={onUploadNarration}
              onDownload={onDownloadNarration}
              onRemove={onRemoveNarration}
              onSaveVoiceUrl={onSaveVoiceUrl}
            />
          </TabsContent>
          <TabsContent value="audio" className="pt-4">
            <MediaKindTab
              kind="audio"
              media={media}
              assetUrl={assetUrl}
              disabled={disabled}
              uploading={uploadingMedia}
              onUpload={onUploadMedia}
              onUpdate={onUpdateMedia}
              onRemove={onRemoveMedia}
            />
          </TabsContent>
          <TabsContent value="video" className="pt-4">
            <MediaKindTab
              kind="video"
              media={media}
              assetUrl={assetUrl}
              disabled={disabled}
              uploading={uploadingMedia}
              onUpload={onUploadMedia}
              onUpdate={onUpdateMedia}
              onRemove={onRemoveMedia}
            />
          </TabsContent>
          <TabsContent value="pdf" className="pt-4">
            <MediaKindTab
              kind="pdf"
              media={media}
              assetUrl={assetUrl}
              disabled={disabled}
              uploading={uploadingMedia}
              onUpload={onUploadMedia}
              onUpdate={onUpdateMedia}
              onRemove={onRemoveMedia}
            />
          </TabsContent>
          <TabsContent value="image" className="pt-4">
            <MediaKindTab
              kind="image"
              media={media}
              assetUrl={assetUrl}
              disabled={disabled}
              uploading={uploadingMedia}
              onUpload={onUploadMedia}
              onUpdate={onUpdateMedia}
              onRemove={onRemoveMedia}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default MediaPanel;
